/**
 * Canonical Account History list-column amounts for all portals.
 * Fee-only companion rows (SENDER_PAID_FEES) store postage/attached as
 * fee-base context for the detail dialog; list columns must not treat
 * those as Reserved/Credited balance movement.
 *
 * Vendor-credit reserve rows (PROMO_RESERVE_*) have no postage/attached;
 * they carry the hold/release amount on reservedFunds only.
 */

export type AccountHistoryListAmountInput = {
  rawType?: string | null;
  requiredPostage?: number | null;
  postage?: number | null;
  attachedFunds?: number | null;
  attachedAmount?: number | null;
  reservedFunds?: number | null;
  postageSystemFee?: number | null;
  attachedFundsFee?: number | null;
  systemFeeAmount?: number | null;
};

export type AccountHistoryListAmounts = {
  isFeeOnlyRow: boolean;
  /** Postage column for the list (0 on fee-only rows). */
  listPostage: number;
  /** Attached Credits column for the list (0 on fee-only rows). */
  listAttached: number;
  /** Reserved/Credited column base (postage + attached, or reservedFunds). */
  totalReservedOrCredited: number;
  totalSystemFees: number;
};

export function isSenderPaidFeesRow(rawType?: string | null): boolean {
  return (rawType ?? '').toUpperCase() === 'SENDER_PAID_FEES';
}

function isPromoReserveRow(rawType?: string | null): boolean {
  return (rawType ?? '').toUpperCase().startsWith('PROMO_RESERVE_');
}

export function getAccountHistoryListAmounts(
  tx: AccountHistoryListAmountInput
): AccountHistoryListAmounts {
  const isFeeOnlyRow = isSenderPaidFeesRow(tx.rawType);
  const requiredPostage = tx.requiredPostage ?? tx.postage ?? 0;
  const attachedFunds = tx.attachedFunds ?? tx.attachedAmount ?? 0;
  const reservedFunds = Math.abs(tx.reservedFunds ?? 0);
  const postageSystemFee = tx.postageSystemFee ?? 0;
  const attachedFundsFee = tx.attachedFundsFee ?? 0;
  const systemFeeAmount = tx.systemFeeAmount ?? 0;
  const totalSystemFees = postageSystemFee + attachedFundsFee + systemFeeAmount;

  if (isFeeOnlyRow) {
    return {
      isFeeOnlyRow: true,
      listPostage: 0,
      listAttached: 0,
      totalReservedOrCredited: 0,
      totalSystemFees,
    };
  }

  const fromPostageAttached = requiredPostage + attachedFunds;
  // Promo reserve hold/release: API puts the move on reservedFunds; postage/attached stay 0.
  const totalReservedOrCredited =
    fromPostageAttached > 0 ? fromPostageAttached : reservedFunds;

  return {
    isFeeOnlyRow: false,
    listPostage: requiredPostage,
    listAttached: attachedFunds,
    totalReservedOrCredited,
    totalSystemFees,
  };
}

/** True when the row is an Available↔Withdraw campaign reserve move (Actual unchanged). */
export function isPromoReserveHistoryRow(rawType?: string | null): boolean {
  return isPromoReserveRow(rawType);
}
